import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPost } from '../lib/api.js';
import type {
  CommissionBasis,
  CommissionDeduction,
  CommissionKind,
  CommissionPaymentRoute,
  SetCourseCommissionBody,
  SetProviderCommissionBody,
} from '../lib/commission.js';

// Response shapes for the 6a/6b commission endpoints. Declared here (web-side)
// because the API keeps these DTOs feature-local; promoting them to
// @admitly/shared would mean editing a backend file. They mirror
// apps/api/src/features/pricing/commissions.service.ts exactly.
//
// The POST bodies live in lib/commission.ts instead: parseCommissionForm is what
// PRODUCES them, it is pure, and it is the tested boundary — so the type belongs
// where its producer does, or this file would have to import backwards.

/** The fields every commission-terms row carries. */
export type CommissionTerms = {
  id: string;
  kind: CommissionKind;
  rateBp: number | null;
  flatAmountCents: number | null;
  basis: CommissionBasis;
  basisMonths: number | null;
  deduction: CommissionDeduction | null;
  paymentRoute: CommissionPaymentRoute | null;
  studentVisaForcesDirect: boolean;
  notes: string | null;
  validFrom: string; // YYYY-MM-DD
  createdBy: string | null;
  createdAt: string; // ISO 8601
};

/** History rows carry the editor's real name; the POST response does not. */
export type CommissionTermsVersion = CommissionTerms & { createdByName: string | null };

/** The POST return: the scope columns, and no editor-name join. */
export type CommissionTermsResponse = CommissionTerms & {
  masterProviderId: string | null;
  masterCourseId: string | null;
};

export type CommissionOverviewCourse = {
  masterCourseId: string;
  courseName: string;
  /** RAW: kind is intact, never collapsed — an 'inherit'/'none' is itself. */
  override: CommissionTerms | null;
  /** The pre-6a free-text note. Reference only — this screen never writes it. */
  legacyText: string | null;
};

export type CommissionOverviewCollege = {
  providerId: string;
  providerName: string;
  current: CommissionTerms | null;
  courses: CommissionOverviewCourse[];
};

export type CommissionOverviewResponse = {
  colleges: CommissionOverviewCollege[];
  /** YYYY-MM-DD the rows were resolved as-of, in Australia/Sydney. */
  asOf: string;
};

// The overview is a SIBLING of the history branches rather than their parent (the
// pricing ladder nests history under the list key). A college can have dozens of
// course histories; invalidating the overview after one provider save must not
// blow them all away.
const commissionKey = ['admin', 'commission'] as const;
const overviewKey = [...commissionKey, 'overview'] as const;
const providerHistoryKey = (providerId: string) =>
  [...commissionKey, 'providers', providerId, 'history'] as const;
const courseHistoryKey = (masterCourseId: string) =>
  [...commissionKey, 'courses', masterCourseId, 'history'] as const;

/** Every visible college with its current deal and its courses' overrides (6b). */
export function useCommissionOverview() {
  return useQuery({
    queryKey: overviewKey,
    queryFn: () => apiGet<CommissionOverviewResponse>('/admin/pricing/commission/overview'),
    staleTime: 30_000,
  });
}

/** One college's terms history, newest-first. Disabled until one is selected. */
export function useProviderCommissionHistory(providerId: string | null) {
  return useQuery({
    queryKey: providerId
      ? providerHistoryKey(providerId)
      : [...commissionKey, 'providers', '__none__', 'history'],
    queryFn: () =>
      apiGet<{ history: CommissionTermsVersion[] }>(
        `/admin/pricing/providers/${providerId}/commission-terms`,
      ),
    enabled: !!providerId,
  });
}

/**
 * One course override's history, newest-first. Fetched ON DEMAND: the caller
 * only mounts the component that calls this once a row's History is opened, so
 * mounting IS the fetch. `enabled` is the defensive guard.
 */
export function useCourseCommissionHistory(masterCourseId: string | null) {
  return useQuery({
    queryKey: masterCourseId
      ? courseHistoryKey(masterCourseId)
      : [...commissionKey, 'courses', '__none__', 'history'],
    queryFn: () =>
      apiGet<{ history: CommissionTermsVersion[] }>(
        `/admin/pricing/courses/${masterCourseId}/commission-terms`,
      ),
    enabled: !!masterCourseId,
  });
}

/** Set / end a college's commission deal — always a new effective-dated version. */
export function useSetProviderCommissionTerms() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      providerId,
      body,
    }: {
      providerId: string;
      body: SetProviderCommissionBody;
    }) =>
      apiPost<{ commissionTerms: CommissionTermsResponse }>(
        `/admin/pricing/providers/${providerId}/commission-terms`,
        body,
      ),
    onSuccess: (_data, { providerId }) => {
      qc.invalidateQueries({ queryKey: overviewKey });
      qc.invalidateQueries({ queryKey: providerHistoryKey(providerId) });
    },
  });
}

/** Set / end / retire one course's special deal. */
export function useSetCourseCommissionTerms() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      masterCourseId,
      body,
    }: {
      masterCourseId: string;
      body: SetCourseCommissionBody;
    }) =>
      apiPost<{ commissionTerms: CommissionTermsResponse }>(
        `/admin/pricing/courses/${masterCourseId}/commission-terms`,
        body,
      ),
    onSuccess: (_data, { masterCourseId }) => {
      qc.invalidateQueries({ queryKey: overviewKey });
      qc.invalidateQueries({ queryKey: courseHistoryKey(masterCourseId) });
    },
  });
}
